import isEmpty from 'lodash/isEmpty';
import { FC, useEffect, useRef, useState } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';

import { FormBlock } from '@/widgets/ServiceFormTemplate';

import { isCountryUkraine, isRegionKyiv, isRegionSevastopol } from '@/features/ui/ProfileChapters';

import { SelectOption } from '@/shared/types';
import { ConditionView, FormSelect, FormTextInput, Loader } from '@/shared/ui';

import { ExtendedCriminalRecordExtractSchema } from '@/modules/iac/pages/services/ExtendedCriminalRecord/model/types';

import { getCitySelect, getCountrySelect, getDistrictSelect, getRegionSelect } from '@/api/apiService';
import { RegisteredAddress } from '@/api/types';

type Props = {
  defaultRegistrationAddress: RegisteredAddress;
};

type RegistrationPlaceFormValues = Omit<
  ExtendedCriminalRecordExtractSchema,
  'registeredCountry' | 'registeredRegion' | 'registeredDistrict' | 'registeredCity'
> & {
  registeredCountry: SelectOption | null;
  registeredRegion: SelectOption | null;
  registeredDistrict: SelectOption | null;
  registeredCity: SelectOption | string | null;
};

export const RegistrationPlace: FC<Props> = ({ defaultRegistrationAddress }) => {
  const { control, getValues, setValue } = useFormContext<RegistrationPlaceFormValues>();
  const [optionsCountry, setOptionsCountry] = useState<SelectOption[]>([]);
  const [optionsRegion, setOptionsRegion] = useState<SelectOption[]>([]);
  const [optionsDistrict, setOptionsDistrict] = useState<SelectOption[]>([]);
  const [optionsCity, setOptionsCity] = useState<SelectOption[]>([]);

  const watchFields = ['registeredCountry', 'registeredRegion', 'registeredDistrict', 'registeredCity'] as const;
  const initialRegistrationPlaceValues = getValues();
  const shouldPrefillFromProfile = !watchFields.some((fieldName) =>
    Object.prototype.hasOwnProperty.call(initialRegistrationPlaceValues, fieldName),
  );
  const watchDefaultValues = shouldPrefillFromProfile
    ? {
        registeredCountry: defaultRegistrationAddress.country ?? null,
        registeredRegion: defaultRegistrationAddress.region ?? null,
        registeredDistrict: defaultRegistrationAddress.district ?? null,
        registeredCity: defaultRegistrationAddress.settlement ?? null,
      }
    : {
        registeredCountry: initialRegistrationPlaceValues.registeredCountry ?? null,
        registeredRegion: initialRegistrationPlaceValues.registeredRegion ?? null,
        registeredDistrict: initialRegistrationPlaceValues.registeredDistrict ?? null,
        registeredCity: initialRegistrationPlaceValues.registeredCity ?? null,
      };

  const [registeredCountry, registeredRegion, registeredDistrict, registeredCity] = useWatch<
    RegistrationPlaceFormValues,
    typeof watchFields
  >({
    control,
    name: watchFields,
    defaultValue: watchDefaultValues,
  });

  const resolvedRegisteredCountry = registeredCountry ?? getValues('registeredCountry') ?? null;
  const resolvedRegisteredRegion = registeredRegion ?? getValues('registeredRegion') ?? null;
  const resolvedRegisteredDistrict = registeredDistrict ?? getValues('registeredDistrict') ?? null;

  const isUkraine: boolean = isCountryUkraine(resolvedRegisteredCountry?.id ?? '');
  const isKyiv = isRegionKyiv(resolvedRegisteredRegion?.id ?? '');
  const isSevastopol = isRegionSevastopol(resolvedRegisteredRegion?.id ?? '');
  const isDistrictDisabled: boolean = !isUkraine || !resolvedRegisteredRegion || isKyiv || isSevastopol;

  const isInitializedRef = useRef(false);
  const prevValuesRef = useRef<{
    countryId: string | null;
    regionId: string | null;
    districtId: string | null;
    isUkraine: boolean;
  }>({
    countryId: null,
    regionId: null,
    districtId: null,
    isUkraine: false,
  });

  useEffect(() => {
    getCountrySelect(setOptionsCountry);

    if (isInitializedRef.current) return;

    const currentValues = getValues();
    if (shouldPrefillFromProfile) {
      const initialCountry = currentValues.registeredCountry ?? defaultRegistrationAddress.country ?? null;
      const initialIsUkraine = isCountryUkraine(initialCountry?.id ?? '');

      if (!currentValues.registeredCountry?.id && defaultRegistrationAddress.country) {
        setValue('registeredCountry', defaultRegistrationAddress.country);
      }

      if (initialIsUkraine) {
        if (!currentValues.registeredRegion?.id && defaultRegistrationAddress.region) {
          setValue('registeredRegion', defaultRegistrationAddress.region);
          getDistrictSelect(defaultRegistrationAddress.region.id as string, setOptionsDistrict);
        }

        if (!currentValues.registeredDistrict?.id && defaultRegistrationAddress.district) {
          setValue('registeredDistrict', defaultRegistrationAddress.district);
          getCitySelect(defaultRegistrationAddress.district.id as string, setOptionsCity);
        }
      } else {
        currentValues.registeredRegion && setValue('registeredRegion', null);
        currentValues.registeredDistrict && setValue('registeredDistrict', null);
      }

      if (!currentValues.registeredCity && defaultRegistrationAddress.settlement) {
        setValue('registeredCity', defaultRegistrationAddress.settlement);
      }
    }

    const updatedValues = getValues();

    prevValuesRef.current = {
      countryId: updatedValues.registeredCountry?.id ?? null,
      regionId: updatedValues.registeredRegion?.id ?? null,
      districtId: updatedValues.registeredDistrict?.id ?? null,
      isUkraine: isCountryUkraine(updatedValues.registeredCountry?.id ?? ''),
    };

    isInitializedRef.current = true;
  }, []);

  useEffect(() => {
    if (isUkraine && !optionsRegion.length) {
      getRegionSelect(setOptionsRegion);
    }
  }, [isUkraine, optionsRegion.length]);

  useEffect(() => {
    if (!isInitializedRef.current) return;

    const currentValues = getValues();
    const currentCountryId = currentValues.registeredCountry?.id ?? null;
    const currentRegion = currentValues.registeredRegion;
    const currentDistrict = currentValues.registeredDistrict;
    const currentCity = currentValues.registeredCity;

    if (currentCountryId === prevValuesRef.current.countryId) return;

    const isUkraineNow = isCountryUkraine(currentCountryId ?? '');

    if (!isUkraineNow) {
      currentRegion && setValue('registeredRegion', null);
      currentDistrict && setValue('registeredDistrict', null);
      currentCity && setValue('registeredCity', null);
      setOptionsRegion([]);
      setOptionsDistrict([]);
      setOptionsCity([]);
    } else if (!prevValuesRef.current.isUkraine) {
      currentCity && setValue('registeredCity', null);
    }

    prevValuesRef.current.countryId = currentCountryId;
    prevValuesRef.current.isUkraine = isUkraineNow;
  }, [registeredCountry, registeredDistrict, registeredRegion, registeredCity]);

  useEffect(() => {
    if (!isInitializedRef.current) return;

    const currentValues = getValues();
    const currentRegion = currentValues.registeredRegion;
    const currentDistrict = currentValues.registeredDistrict;
    const currentCity = currentValues.registeredCity;
    const currentCountryId = currentValues.registeredCountry?.id ?? '';
    const currentRegionId = currentRegion?.id ?? null;
    const isUkraineNow = isCountryUkraine(currentCountryId);
    if (currentRegionId === prevValuesRef.current.regionId) return;

    currentDistrict && setValue('registeredDistrict', null);
    currentCity && setValue('registeredCity', null);
    setOptionsDistrict([]);
    setOptionsCity([]);

    if (currentRegionId && isUkraineNow) {
      if (isRegionKyiv(currentRegionId) || isRegionSevastopol(currentRegionId)) {
        currentRegion && setValue('registeredCity', currentRegion);
      }
    }

    prevValuesRef.current.regionId = currentRegionId;
  }, [registeredCountry, registeredDistrict, registeredRegion, registeredCity]);

  useEffect(() => {
    if (!isInitializedRef.current) return;

    const currentValues = getValues();
    const currentDistrictId = currentValues.registeredDistrict?.id ?? null;
    const currentCity = currentValues.registeredCity;
    if (currentDistrictId === prevValuesRef.current.districtId) return;

    currentCity && setValue('registeredCity', null);
    setOptionsCity([]);

    prevValuesRef.current.districtId = currentDistrictId;
  }, [registeredDistrict, registeredCity]);

  useEffect(() => {
    const currentValues = getValues();
    const currentRegion = currentValues.registeredRegion;
    const currentCity = currentValues.registeredCity;
    const currentRegionId = currentRegion?.id ?? '';

    if ((isRegionKyiv(currentRegionId) || isRegionSevastopol(currentRegionId)) && currentRegion && !currentCity) {
      setValue('registeredCity', currentRegion);
    }
  }, [registeredRegion, registeredCity]);

  useEffect(() => {
    const currentValues = getValues();
    const currentCountryId = currentValues.registeredCountry?.id ?? '';
    const currentRegionId = currentValues.registeredRegion?.id ?? '';
    const isUkraineNow = isCountryUkraine(currentCountryId);
    const isKyivNow = isRegionKyiv(currentRegionId);
    const isSevastopolNow = isRegionSevastopol(currentRegionId);

    if (!isUkraineNow || !currentRegionId || isKyivNow || isSevastopolNow || optionsDistrict.length) return;

    getDistrictSelect(currentRegionId, setOptionsDistrict);
  }, [registeredCountry, registeredRegion, optionsDistrict.length]);

  useEffect(() => {
    const currentValues = getValues();
    const currentCountryId = currentValues.registeredCountry?.id ?? '';
    const currentRegionId = currentValues.registeredRegion?.id ?? '';
    const currentDistrictId = currentValues.registeredDistrict?.id ?? '';
    const isUkraineNow = isCountryUkraine(currentCountryId);
    const isKyivNow = isRegionKyiv(currentRegionId);
    const isSevastopolNow = isRegionSevastopol(currentRegionId);

    if (!isUkraineNow || !currentDistrictId || isKyivNow || isSevastopolNow || optionsCity.length) return;

    getCitySelect(currentDistrictId, setOptionsCity);
  }, [registeredCountry, registeredRegion, registeredDistrict, optionsCity.length]);

  return (
    <ConditionView if={!isEmpty(optionsCountry)} else={<Loader centered />}>
      <FormBlock title="Місце реєстрації">
        <div className="birth-place-and-registration-step__section">
          <FormSelect
            name="registeredCountry"
            control={control}
            label="Країна"
            options={optionsCountry}
            required
            defaultValue={shouldPrefillFromProfile ? (defaultRegistrationAddress.country ?? null) : null}
          />
          <FormSelect
            name="registeredRegion"
            control={control}
            label="Область"
            options={optionsRegion}
            required={isUkraine}
            disabled={!isUkraine}
            defaultValue={shouldPrefillFromProfile ? (defaultRegistrationAddress.region ?? null) : null}
          />
          <FormSelect
            name="registeredDistrict"
            control={control}
            label="Район"
            options={optionsDistrict}
            required={isUkraine}
            disabled={isDistrictDisabled}
            defaultValue={shouldPrefillFromProfile ? (defaultRegistrationAddress.district ?? null) : null}
          />
          <ConditionView
            if={isUkraine}
            else={
              <FormTextInput
                name="registeredCity"
                control={control}
                label="Населений пункт"
                required
                defaultValue={shouldPrefillFromProfile ? defaultRegistrationAddress.settlement : null}
              />
            }
          >
            <FormSelect
              name="registeredCity"
              control={control}
              label="Населений пункт"
              options={isKyiv || isSevastopol ? optionsRegion : optionsCity}
              disabled={isDistrictDisabled || !resolvedRegisteredDistrict}
              required
              defaultValue={shouldPrefillFromProfile ? defaultRegistrationAddress.settlement : null}
            />
          </ConditionView>
        </div>
      </FormBlock>
    </ConditionView>
  );
};
